import { useEffect, useState } from "react";
import { workspaceIdSchema, type WorkspaceId } from "./workspaces.js";

const STORAGE_KEY = "admin.nav.lastUsedWorkspaceId";

export function readStoredWorkspaceId(): WorkspaceId | undefined {
  try {
    const parsed = workspaceIdSchema.safeParse(
      window.sessionStorage.getItem(STORAGE_KEY),
    );
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

function storeWorkspaceId(workspaceId: WorkspaceId): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, workspaceId);
  } catch {
    // Ignore storage failures (e.g. private browsing); memory still works in-session.
  }
}

/**
 * Remembers the most recently used workspace for the session: the current
 * workspace when there is one, otherwise the last used one — or undefined when
 * nothing has been used yet, in which case callers fall back to an available
 * workspace (see `useWorkspaceOptions`).
 */
export function useRememberedWorkspaceId(
  currentWorkspaceId: WorkspaceId | undefined,
): WorkspaceId | undefined {
  const [lastUsedWorkspaceId, setLastUsedWorkspaceId] = useState(
    readStoredWorkspaceId,
  );

  useEffect(() => {
    if (currentWorkspaceId && currentWorkspaceId !== lastUsedWorkspaceId) {
      storeWorkspaceId(currentWorkspaceId);
      setLastUsedWorkspaceId(currentWorkspaceId);
    }
  }, [currentWorkspaceId, lastUsedWorkspaceId]);

  return currentWorkspaceId ?? lastUsedWorkspaceId;
}
